import {
  Button,
  Checkbox,
  DrawerCard,
  DrawerField,
  DrawerTitle,
  TextArea,
  bindControllerState,
} from "@mixitone/components";
import ClearIcon from "@mixitone/icons/clear.svg";
import LinkIcon from "@mixitone/icons/link.svg";
import UserIcon from "@mixitone/icons/user.svg";
import type { Note, Player } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { ClubPlayerGroupLevelControl } from "components/ClubPlayerGroupLevelControl";
import { PlayerDrawerController } from "components/Drawers/NightPlayer/PlayerDrawerController";
import PlayerDropdown from "components/Drawers/NightPlayer/PlayerDropdown";
import PlayerDot from "components/common/PlayerDot";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import CrossIcon from "components/icons/CrossIcon";
import TickIcon from "components/icons/TickIcon";
import { injectModal } from "lib/injectParams";
import { useCallback } from "react";

interface Props {
  token: string;
  clubId: string;
  id: string;
}

const PlayerDrawerContent = ApplicationView(() => {
  const controller = PlayerDrawerController.use();
  const {
    player,
    clubPlayer,
    clubPlayersInSession,
    setGames,
    loading,
    toggling,
    notes,
    addingNote,
    updating,
  } = controller.state;
  const { bind } = bindControllerState(controller, "newNote");
  const { pairedClubPlayer } = controller;

  const handlePlayerClick = useCallback(
    (player: Player) => () => {
      controller.actionSwitchDrawer(player.club_player_id!);
    },
    [],
  );

  if (loading) {
    return (
      <div className="flex animate-pulse flex-col gap-4">
        <DrawerCard>
          <DrawerField label="Name">&nbsp;</DrawerField>
          <DrawerField label="Group">&nbsp;</DrawerField>
          <hr />
          <DrawerField label="Pairing">&nbsp;</DrawerField>
          <hr />
          <DrawerField label="Notes">
            <div className="max-h-[150px] w-full overflow-y-auto">
              <div className="w-full rounded bg-gray-50 p-2 text-gray-50">&nbsp;</div>
            </div>
          </DrawerField>
        </DrawerCard>
        <DrawerCard>
          <DrawerField label="Games played">&nbsp;</DrawerField>
          <hr />
          <DrawerField label="Sat out">&nbsp;</DrawerField>
        </DrawerCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <DrawerCard>
        <DrawerField label="Name">
          <div className="flex w-full items-center justify-between gap-2">
            <span>{clubPlayer.name}</span>
            <Button
              kind="action"
              title="Open member profile"
              onClick={() => controller.actionOpenMemberDrawer()}
            >
              <span className="inline-flex items-center gap-1">
                Member
                <LinkIcon className="h-4 w-4 stroke-current" aria-hidden />
              </span>
            </Button>
          </div>
        </DrawerField>
        <hr />
        <DrawerField label="Group">
          <ClubPlayerGroupLevelControl
            player={clubPlayer}
            clubGroups={controller.state.club.groups}
            onSelectGroup={(nextGroup) => controller.actionMoveToGroup(nextGroup)}
            onSelectLevel={(level) => controller.actionUpdatePlayerGraduationLevel(level)}
            className="flex items-center gap-2 whitespace-nowrap py-0 [&_.group-name]:inline"
          />
        </DrawerField>
        <hr />
        <DrawerField label="Notes">
          <div className="max-h-[200px] w-full overflow-y-auto">
            {(!notes || notes.length === 0) && <div className="text-gray-500 italic">No notes yet</div>}

            {notes && notes.length > 0 && (
              <ul className="space-y-2">
                {notes.map((note: Note) => (
                  <li key={note.id} className="flex items-center gap-2 rounded bg-gray-50 p-2">
                    <Checkbox
                      checked={!!note.resolved_at}
                      onChange={() => controller.actionToggleNoteResolved(note)}
                      className="mt-1"
                    />
                    <div className={`flex-1 ${note.resolved_at ? "text-gray-400 line-through" : ""}`}>
                      {note.note}
                    </div>
                    <Button
                      className="h-[20px] w-[20px] p-0"
                      kind="danger"
                      onClick={() => controller.actionDeleteNote(note)}
                    >
                      <CrossIcon className="h-[7px]" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            {!addingNote ? (
              <div className="mt-2">
                <Button kind="action" onClick={() => controller.actionToggleAddingNote(true)}>
                  Add note
                </Button>
              </div>
            ) : (
              <div className="mt-2 flex flex-col">
                <TextArea
                  className="h-[80px] rounded-b-none"
                  {...bind("note")}
                  placeholder="Enter a new note..."
                />
                <div className="flex items-center justify-end gap-1 bg-gray-100 p-1 text-sm">
                  <Button
                    className="h-[20px] w-[28px] p-0"
                    disabled={updating.has("notes")}
                    onClick={() => controller.actionAddNote()}
                    spinner={updating.has("notes")}
                  >
                    <TickIcon className="w-[10px]" />
                  </Button>
                  <Button
                    className="ml-2"
                    kind="plain"
                    onClick={() => controller.actionToggleAddingNote(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DrawerField>
      </DrawerCard>

      {!player && (
        <DrawerCard>
          <DrawerField label="Session">
            <Button kind="accent" onClick={() => controller.actionJoin()} spinner={toggling}>
              Join session
            </Button>
          </DrawerField>
        </DrawerCard>
      )}

      {player && (
        <>
          <DrawerCard className="relative z-10">
            <DrawerField label="Session">
              {player.active && (
                <Button kind="danger" onClick={() => controller.actionRetire()} spinner={toggling}>
                  Retire
                </Button>
              )}
              {!player.active && (
                <Button kind="accent" onClick={() => controller.actionJoin()} spinner={toggling}>
                  Join session
                </Button>
              )}
            </DrawerField>
            <DrawerField label="Pairing">
              <PlayerDropdown
                clubPlayers={clubPlayersInSession}
                value={pairedClubPlayer || null}
                onChange={(player) => controller.actionSetPairedPlayer(player)}
                canRegisterFromSearch={false}
              />
              <Button kind="plain" onClick={() => controller.actionSetPairedPlayer(null)}>
                <ClearIcon className="w-[20px] fill-gray-500" />
              </Button>
            </DrawerField>
            <hr />
            <DrawerField label="Games played">{player.timesPlayed}</DrawerField>
            <hr />
            <DrawerField label="Sat out">{player.timesOut}</DrawerField>
          </DrawerCard>

          <DrawerCard>
            <div className="flex flex-col gap-2">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: old */}
              <label className="w-1/3 text-gray-600">Games</label>
              <table>
                <tbody>
                  {setGames.map(({ gameSet, players }, index) => {
                    return (
                      <tr key={`game-set-game-${index}`} className="border-b border-b-gray-200">
                        <td className="w-[60px] py-2 align-top">Set {gameSet.index! + 1}</td>
                        <td className="py-2 text-gray-500">
                          {players[0] && (
                            <PlayerDot onClick={handlePlayerClick(players[0])} inline player={players[0]} />
                          )}
                        </td>
                        <td className="py-2 text-gray-500">
                          {players[1] && (
                            <PlayerDot onClick={handlePlayerClick(players[1])} inline player={players[1]} />
                          )}
                        </td>
                        <td className="py-2 text-gray-500">
                          {players[2] && (
                            <PlayerDot onClick={handlePlayerClick(players[2])} inline player={players[2]} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </DrawerCard>
        </>
      )}
    </div>
  );
});

const NightPlayer = ({ token }: Props) => {
  const controller = PlayerDrawerController.use();
  const { loading } = controller.state;

  return (
    <RoutedDrawer spinner={loading}>
      <DrawerTitle
        icon={<UserIcon className="mr-2 w-[36px] stroke-gray-500" />}
        contextLink={<DrawerContextLink context="sessionPlayers" params={{ token }} />}
      >
        Player profile
      </DrawerTitle>
      <PlayerDrawerContent />
    </RoutedDrawer>
  );
};

export default injectModal(PlayerDrawerController.scope(ApplicationView(NightPlayer)));
